import { Button } from "@nimit9/signet-ui/components/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@nimit9/signet-ui/components/select"
import { casHoldingBodies, type ParsedCas, planCasImport } from "@paisa/parsers"
import { useQueryClient } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import toast from "react-hot-toast"
import { useCurrencyFormat } from "@/hooks/use-currency-format"
import { useInvestments } from "@/hooks/use-investments"
import { useOwnerLabels } from "@/hooks/use-owner-labels"
import { api } from "@/lib/api"
import { ApiError } from "@/lib/api-errors"

/**
 * A CDSL CAS dropped into the statement dialog: holdings, not transactions.
 * Parsed on the device; only the new holdings go to the API (same plan as
 * MCP import_statement from='cas').
 */
export function StepCas({
  cas,
  onBack,
  onDone,
}: {
  cas: ParsedCas
  onBack: () => void
  onDone: () => void
}) {
  const fmt = useCurrencyFormat()
  const ownerLabels = useOwnerLabels()
  const qc = useQueryClient()
  const { data, isLoading } = useInvestments()
  const [owner, setOwner] = useState("user_1")
  const [saving, setSaving] = useState(false)
  const plan = useMemo(() => planCasImport(cas, data?.holdings ?? []), [cas, data])
  const addCount = plan.toAdd.length + (plan.nps !== null ? 1 : 0)

  async function add() {
    setSaving(true)
    try {
      for (const body of casHoldingBodies(plan, owner))
        await api.post("/investments/holdings", body)
      let note = ""
      try {
        await api.post("/net-worth/auto", {})
      } catch (err) {
        // 409: this month's net worth was typed in by hand; leave it.
        if (!(err instanceof ApiError && err.status === 409)) throw err
        note = " Net worth for this month was entered by hand, so it wasn't changed."
      }
      qc.invalidateQueries({ queryKey: ["investments"] })
      qc.invalidateQueries({ queryKey: ["net-worth"] })
      toast.success(`Added ${addCount} holding${addCount === 1 ? "" : "s"}.${note}`)
      onDone()
    } catch {
      toast.error("Couldn't add the holdings")
    } finally {
      setSaving(false)
    }
  }

  if (!cas.reconciled) {
    return (
      <div className="space-y-4 p-6">
        <p className="text-body text-text-primary">
          This CAS doesn't add up: its holdings don't match CDSL's own demat total, so nothing was
          imported.
        </p>
        <Button variant="outline" size="touch" onClick={onBack}>
          Back
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4 overflow-y-auto p-6" data-testid="step-cas">
      <div>
        <p className="text-callout font-semibold text-text-primary">
          CDSL holdings statement{cas.asOf ? ` · as on ${cas.asOf}` : ""}
        </p>
        <p className="text-caption text-text-secondary">
          {cas.totalValue !== null ? `${fmt(cas.totalValue)} in total. ` : ""}Read on this device.
        </p>
      </div>
      {isLoading ? (
        <p className="text-caption text-text-muted">Checking what you already track…</p>
      ) : (
        <>
          <p className="text-body text-text-primary">
            {plan.tracked.length} already tracked · {addCount} new
          </p>
          {addCount > 0 && (
            <ul className="divide-y divide-border-subtle">
              {plan.toAdd.slice(0, 10).map((h) => (
                <li key={h.isin} className="flex items-center justify-between gap-3 py-1.5">
                  <span className="min-w-0 truncate text-caption text-text-primary">
                    {h.name || h.isin}
                  </span>
                  <span className="shrink-0 text-caption text-text-secondary">{fmt(h.value)}</span>
                </li>
              ))}
              {plan.nps !== null && (
                <li className="flex items-center justify-between gap-3 py-1.5">
                  <span className="text-caption text-text-primary">NPS</span>
                  <span className="text-caption text-text-secondary">{fmt(plan.nps)}</span>
                </li>
              )}
            </ul>
          )}
          {addCount > 0 && (
            <p className="text-caption text-text-muted">
              The CAS has no purchase prices, so new holdings start with invested = current value.
            </p>
          )}
        </>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {addCount > 0 && (
          <Select value={owner} onValueChange={(v) => v && setOwner(v)} items={ownerLabels}>
            <SelectTrigger size="sm" aria-label="Owner of the new holdings">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(ownerLabels).map(([key, label]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="flex-1" />
        <Button variant="outline" size="touch" onClick={onBack}>
          Back
        </Button>
        {addCount > 0 ? (
          <Button size="touch" onClick={add} disabled={saving || isLoading}>
            {saving ? "Adding…" : `Add ${addCount} holding${addCount === 1 ? "" : "s"}`}
          </Button>
        ) : (
          <Button size="touch" onClick={onDone} disabled={isLoading}>
            Done
          </Button>
        )}
      </div>
    </div>
  )
}
